/* ============================================================
   Home Wellbeinn — Recomendador express (sections/uxkid-home-recommender.liquid
   + snippets/uxkid-recommender-sheet.liquid). Figma: componente bottom sheet
   2332:903 (variantes "question" 2332:902 y "packs" 2178:7654), sección
   "Section - forms" 2057:1970 / 2332:834. Spec: wellbeinn-recomendador-spec.md.

   Estructura del archivo:
   1. Lógica PURA (testeable con Node, sin DOM): resolución de versión del
      pack, construcción/reanudación de estado, persistencia en
      sessionStorage/localStorage. Exportada como `UxkidReco` (o
      module.exports en Node) para que scratchpad/tests/recommender.test.js
      la pueda importar sin cargar un DOM.
   2. Wiring de DOM (chips, bottom sheet, swipe-to-dismiss, focus trap):
      solo corre si `document` existe, así el bloque 1 se puede testear solo.

   Estado persistido en sessionStorage (clave wb_reco_state):
     { goal, freq, start, step, completed, mode }
   Corrección de Manuel (2026-09-22, tarde): el flujo depende del
   breakpoint — YA NO es un único flujo compartido por sheet y diálogo.
   `mode` ('sheet'|'dialog', resuelto con isDialogMode() — ver más abajo)
   queda grabado en el estado para poder detectar cuando cambió (el
   usuario cerró en mobile y volvió a abrir en desktop, o viceversa, o
   directamente resizeó la ventana entre visitas) y reiniciar el flujo
   desde el primer paso del modo actual en vez de reanudar en un paso que
   ese modo ni siquiera recorre.
   - MOBILE (mode 'sheet'): flujo original (previo a la corrección de
     Figma "Home — Dialog" 2430:4925) — "freq" (P1, frecuencia) → "start"
     (P2, cómo empezar) → "result". SIN paso de email.
   - DIÁLOGO (mode 'dialog', ≥768px): flujo de ese Figma 2430:4925 —
     "start" (única pregunta) → "result" (con CTA que avanza a "email" si
     ese paso está activo y todavía no hay suscripción) → "email". SIN
     paso de frecuencia.
   `step` es uno de 'freq' | 'start' | 'result' | 'email': "freq" solo es
   alcanzable en mode 'sheet' y "email" solo en mode 'dialog' (ver
   snippets/uxkid-recommender-sheet.liquid, que tiene los 4 divs pero cada
   modo solo transiciona entre los 3 que le corresponden). "email" además
   sigue dependiendo del bloque `email_step` (máx. 1) agregado a la
   sección: sin él, el diálogo también es de 2 pasos.
   `wb_goal` en localStorage es la clave que usa (o usará) el popup v2 del
   objetivo elegido; acá se lee para premarcar el chip al cargar la home y
   se escribe cada vez que se toca un chip.
   ============================================================ */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'wb_reco_state';
  var GOAL_KEY = 'wb_goal';

  /* resolveVersion — Spec §3 (regla original, restaurada 2026-09-22 tarde
     junto con el paso "freq" en mobile): decide la versión del pack
     (full/entry).
     Inputs: start ('completo'|'sencillo'|'auto' — respuesta de P2/P3),
             freq ('diario'|'semana'|'puntual' — respuesta de P1, SOLO
             mobile; en el diálogo siempre llega `null`, no hay paso de
             frecuencia ahí).
     Output: 'full' | 'entry'.
     Regla: completo→full; sencillo→entry; auto→ freq=diario ? full :
     entry (en diálogo, sin freq, 'auto' cae siempre a 'entry': opción más
     conservadora, nunca empuja al pack caro sin una respuesta explícita). */
  function resolveVersion(start, freq) {
    if (start === 'completo') return 'full';
    if (start === 'sencillo') return 'entry';
    if (start === 'auto') return freq === 'diario' ? 'full' : 'entry';
    return 'entry';
  }

  /* buildInitialState — estado en blanco para un objetivo sin historial.
     Inputs: goal (string), mode ('sheet'|'dialog', ver isDialogMode()).
     Output: estado nuevo, nada respondido, arrancando en el primer paso
     de ESE modo: "freq" en mobile (sheet), "start" en el diálogo (no
     tiene paso de frecuencia). */
  function buildInitialState(goal, mode) {
    var firstStep = mode === 'dialog' ? 'start' : 'freq';
    return { goal: goal, freq: null, start: null, step: firstStep, completed: false, mode: mode };
  }

  /* resumeState — decide con qué estado abrir el sheet/diálogo al tocar un
     chip. Inputs: goal (objetivo tocado ahora), saved (estado persistido o
     null), mode (modo ACTUAL, 'sheet'|'dialog'). Output: estado a usar
     para abrir.
     - Sin estado previo → arranca en blanco (buildInitialState).
     - Mismo goal, MISMO modo que el guardado, Y flujo incompleto (paso
       válido de ese modo, sin completar) → retoma exactamente donde
       estaba (mismo paso, mismas respuestas). Los pasos válidos por modo
       son 'freq'/'start'/'result' en sheet y 'start'/'result'/'email' en
       diálogo: si el modo cambió (p.ej. cerró en mobile con "freq"
       pendiente y volvió a abrir en desktop, o al revés con "email"
       pendiente) el paso guardado ni siquiera existe en el modo actual,
       así que NUNCA cuenta como incompleto — cubre también el pedido de
       Manuel de que "email" jamás sea alcanzable en mobile aunque el
       usuario venga de haber abierto el diálogo y luego achique la
       ventana. Cubre además el caso "volvió de recargar la página tras
       el form de email" (diálogo): ese reload no pasa por acá (lo
       resuelve initRecommender al leer el marcador de éxito), pero si el
       usuario cierra el diálogo en el paso email y vuelve a tocar el
       chip SIN cambiar de modo, retoma ahí en vez de perder "start".
     - Goal distinto, modo distinto, O mismo goal+modo con el flujo ya
       completo → arranca en el primer paso del modo actual, pero
       conserva freq/start previos como valores premarcados (no se
       pierden, solo se vuelven a mostrar desde el principio). */
  function resumeState(goal, saved, mode) {
    if (!saved || typeof saved !== 'object' || !saved.goal) {
      return buildInitialState(goal, mode);
    }
    var sameGoal = saved.goal === goal;
    var sameMode = saved.mode === mode;
    var validSteps = mode === 'dialog' ? ['start', 'result', 'email'] : ['freq', 'start', 'result'];
    var incomplete = sameMode && !saved.completed && validSteps.indexOf(saved.step) !== -1;
    if (sameGoal && incomplete) {
      return {
        goal: goal,
        freq: saved.freq || null,
        start: saved.start || null,
        step: saved.step,
        completed: false,
        mode: mode
      };
    }
    var firstStep = mode === 'dialog' ? 'start' : 'freq';
    return {
      goal: goal,
      freq: saved.freq || null,
      start: saved.start || null,
      step: firstStep,
      completed: false,
      mode: mode
    };
  }

  /* advanceState — aplica una respuesta y calcula el siguiente paso, según
     el modo grabado en el propio estado (state.mode). Inputs: state
     actual, question ('freq'|'start'|'result'|'email'), value elegido,
     emailEnabled (boolean: bloque email_step presente en la sección; solo
     importa en modo diálogo). Output: estado NUEVO (no muta el original).

     Mobile (state.mode === 'sheet'):
     - Responder 'freq' (P1) avanza a 'start'.
     - Responder 'start' (P2) avanza a 'result' y marca completed: en
       mobile "result" es siempre el último paso, no hay email.

     Diálogo (state.mode === 'dialog'):
     - Responder 'start' (única pregunta) avanza siempre a 'result'; marca
       completed de una si el paso email no está activo (ahí no hay nada
       más que esperar).
     - 'result' con value 'cta' avanza a 'email' (el llamador —ver el
       click handler del CTA en initRecommender— solo dispara esto en
       modo diálogo cuando el paso email está activo y el usuario todavía
       no se suscribió; en mobile, o sin email activo, el CTA se comporta
       como link real o botón inerte y esta función ni se llama).
     - Responder 'email' (cualquier value) siempre avanza a 'result' y
       marca completed: ya sea por el submit real (ver el bloque que
       reanuda tras el reload, más abajo) o por cualquier otro valor. */
  function advanceState(state, question, value, emailEnabled) {
    var next = {
      goal: state.goal,
      freq: state.freq,
      start: state.start,
      step: state.step,
      completed: state.completed,
      mode: state.mode
    };
    if (state.mode === 'dialog') {
      if (question === 'start') {
        next.start = value;
        next.step = 'result';
        next.completed = !emailEnabled;
      } else if (question === 'result') {
        next.step = 'email';
      } else if (question === 'email') {
        next.step = 'result';
        next.completed = true;
      }
    } else {
      if (question === 'freq') {
        next.freq = value;
        next.step = 'start';
      } else if (question === 'start') {
        next.start = value;
        next.step = 'result';
        next.completed = true;
      }
    }
    return next;
  }

  /* progressSegments — cuántos segmentos de la barra de progreso deben
     verse "completos" para un step dado, según el modo.
     Mobile (2 segmentos, flujo original: freq→start→result, ajuste de
     Manuel 2026-09-21): 'freq' 1, 'start'/'result' 2.
     Diálogo (hasta 3 segmentos, Figma "Home — Dialog" 2430:4925): 'start'
     1, 'result' 2, 'email' 3. El 3er segmento lo oculta el JS cuando no
     corresponde (mobile siempre, o diálogo sin paso de email — ver
     initRecommender), así que ese valor no llega a pintarse ahí.
     Output: 1, 2 o 3. */
  function progressSegments(step, mode) {
    if (mode === 'dialog') {
      if (step === 'start') return 1;
      if (step === 'result') return 2;
      return 3; // 'email'
    }
    if (step === 'freq') return 1;
    return 2; // 'start' o 'result'
  }

  /* resolveDragPhase — a qué fase pertenece el gesto de swipe-to-dismiss
     según cuánto se movió el dedo/mouse. Inputs: deltaY (desplazamiento
     vertical acumulado desde el toque inicial, ya clamped a >= 0), slop
     (umbral de arranque en px: por debajo, es un toque/click normal, no
     un drag). Output: 'pending' | 'active'.
     'pending': todavía puede ser un tap (p.ej. tocar la X) — no se debe
     mover el panel ni bloquear el click nativo.
     'active': ya es un drag real — recién acá se mueve el panel y se
     puede frenar el scroll nativo con preventDefault. */
  function resolveDragPhase(deltaY, slop) {
    return deltaY > slop ? 'active' : 'pending';
  }

  /* shouldPreventTouchScroll — si conviene llamar preventDefault() en el
     touchmove nativo para que el gesto no se lo lleve el navegador (scroll,
     back-swipe) antes de llegar al umbral de descarte. Debe ser MUY
     conservador: llamarlo de más es lo que causaba el bug "hay que tocar
     la X dos veces" (preventDefault en el primer touchmove de un toque
     suprime los eventos de mouse sintetizados de ESE toque, click incluido).
     Inputs: phase ('pending'|'active', de resolveDragPhase), deltaY,
     scrollTop (del contenido real). Output: boolean.
     Solo true cuando el drag YA es real (superó el slop), va hacia abajo
     (deltaY > 0) y el contenido está arriba del todo. */
  function shouldPreventTouchScroll(phase, deltaY, scrollTop) {
    return phase === 'active' && deltaY > 0 && scrollTop <= 0;
  }

  /* ---------- Persistencia (try/catch: Safari privado, cuota llena, etc.) ---------- */

  function saveState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      return true;
    } catch (err) {
      return false;
    }
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.goal) return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function saveGoal(goal) {
    try {
      localStorage.setItem(GOAL_KEY, goal);
      return true;
    } catch (err) {
      return false;
    }
  }

  function loadGoal() {
    try {
      return localStorage.getItem(GOAL_KEY);
    } catch (err) {
      return null;
    }
  }

  var UxkidReco = {
    STORAGE_KEY: STORAGE_KEY,
    GOAL_KEY: GOAL_KEY,
    resolveVersion: resolveVersion,
    buildInitialState: buildInitialState,
    resumeState: resumeState,
    advanceState: advanceState,
    progressSegments: progressSegments,
    resolveDragPhase: resolveDragPhase,
    shouldPreventTouchScroll: shouldPreventTouchScroll,
    saveState: saveState,
    loadState: loadState,
    saveGoal: saveGoal,
    loadGoal: loadGoal
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = UxkidReco;
  } else {
    global.UxkidReco = UxkidReco;
  }

  /* ================================================================
     Wiring de DOM. Solo corre en navegador (no hay `document` en el
     test de Node, así que este bloque completo queda sin ejecutar ahí).
     ================================================================ */
  if (typeof document === 'undefined') return;

  /* initRecommender — engancha una sección .uxkid-reco con su sheet.
     Input: elemento raíz [data-uxkid-reco-root]. */
  function initRecommender(root) {
    var sheet = root.querySelector('[data-uxkid-reco-sheet]');
    var panel = sheet && sheet.querySelector('[data-uxkid-reco-panel]');
    var content = sheet && sheet.querySelector('[data-uxkid-reco-content]');
    if (!sheet || !panel) return;

    // Bloque email_step (máx. 1, sections/uxkid-home-recommender.liquid):
    // su presencia controla si "result" puede avanzar a "email" en modo
    // diálogo. En mobile no importa: ahí "email" nunca es alcanzable.
    var emailEnabled = root.getAttribute('data-show-email-step') === 'true';

    var chips = root.querySelectorAll('[data-uxkid-reco-chip]');
    var steps = sheet.querySelectorAll('[data-uxkid-reco-step]');
    var packs = sheet.querySelectorAll('[data-uxkid-reco-pack]');
    // Único botón de cerrar, en la cabecera fija (handle + X): visible en
    // todos los pasos, ya no hay uno por pack.
    var headerCloseBtn = sheet.querySelector('[data-uxkid-reco-drag] [data-uxkid-reco-close]');
    // Rótulo fijo del diálogo (≥768px, ver CSS): mismo lugar que el botón
    // cerrar ("Top bar" del Figma "Dialog (Desktop · Tablet)" 2430:4924).
    // En mobile este elemento queda oculto por CSS (ahí manda la barra de
    // segmentos) y el JS ni lo llena (ver goToStep). Textos literales
    // cortos de interfaz, no copy de marketing.
    // Dos spans (ver snippet): el principal ("Paso X de Y") y uno extra
    // (" · Tu plan recomendado") que el CSS oculta bajo 1200px (Figma: en
    // tablet el rótulo de "result" es solo "Paso 2 de Y", sin el sufijo).
    var dialogLabelMain = sheet.querySelector('[data-uxkid-reco-dialog-label-main]');
    var dialogLabelExtra = sheet.querySelector('[data-uxkid-reco-dialog-label-extra]');
    // Rótulos del diálogo (flujo de 3 pasos con email activo, o de 2 sin
    // él — Figma "Home — Dialog (multi-step form)" 2430:4925). Solo se
    // usan en modo diálogo (ver goToStep); en mobile el flujo es
    // freq→start→result y ese rótulo ni se muestra (CSS).
    var totalDialogSteps = emailEnabled ? 3 : 2;
    var DIALOG_LABELS = {
      start: 'Paso 1 de ' + totalDialogSteps,
      result: 'Paso 2 de ' + totalDialogSteps,
      email: 'Paso 3 de ' + totalDialogSteps
    };
    var lastFocus = null;
    var closeTimer = null;

    /* isDialogMode — corrección de Manuel (2026-09-22 tarde): el flujo
       depende del breakpoint, no hay un único flujo compartido. Se
       evalúa cada vez que hace falta (al abrir/reanudar y al avanzar de
       paso), nunca se cachea en una variable de módulo, porque el modo
       puede cambiar entre una apertura y otra (resize de ventana). */
    function isDialogMode() {
      return !!(global.matchMedia && global.matchMedia('(min-width: 768px)').matches);
    }

    /* Marca visualmente el chip correspondiente a `goal` (o ninguno). */
    function markSelectedChip(goal) {
      chips.forEach(function (chip) {
        chip.classList.toggle('is-selected', chip.getAttribute('data-goal') === goal);
      });
    }

    /* Premarca (visualmente, sin disparar el avance) la opción ya
       respondida de un paso, si la hay. Input: contenedor del step,
       pregunta ('freq'|'start'), valor guardado (o null). */
    function premarkOptions(stepEl, question, value) {
      stepEl.querySelectorAll('[data-uxkid-reco-option][data-question="' + question + '"]').forEach(function (opt) {
        opt.classList.toggle('is-selected', !!value && opt.getAttribute('data-value') === value);
      });
    }

    /* Actualiza la barra de progreso (2 segmentos en mobile, hasta 3 en
       diálogo) de un step. Input: contenedor del step, step, mode. */
    function updateProgress(stepEl, step, mode) {
      var filled = UxkidReco.progressSegments(step, mode);
      stepEl.querySelectorAll('[data-uxkid-reco-seg]').forEach(function (seg) {
        var idx = parseInt(seg.getAttribute('data-uxkid-reco-seg'), 10);
        seg.classList.toggle('is-filled', idx <= filled);
      });
    }

    /* Sincroniza la visibilidad del 3er segmento de la barra de progreso
       (existe en el markup de "start"/"result"/"email" para que el
       diálogo con email activo pueda usar los 3): oculto siempre en
       mobile (ahí el flujo es de 2 segmentos, freq/start/result) y en
       diálogo cuando no hay paso de email (2 de 2 también ahí). */
    function syncSegVisibility(mode) {
      var showThird = mode === 'dialog' && emailEnabled;
      sheet.querySelectorAll('[data-uxkid-reco-seg="3"]').forEach(function (seg) {
        seg.hidden = !showThird;
      });
    }

    /* Muestra un único step ('freq'|'start'|'result'|'email') y oculta el
       resto. También refleja el step activo en el panel
       (data-uxkid-reco-current-step): el CSS del diálogo (≥768px) lo usa
       para variar el ancho máximo (preguntas/email 640px, resultado en 2
       columnas 880px en desktop, 560px los 3 pasos en tablet; ver
       uxkid-home-recommender.css). El rótulo del top bar (Paso X de Y)
       solo aplica en diálogo — en mobile ya queda oculto por CSS, pero
       tampoco hace falta llenarlo (Manuel, punto 4 de la corrección). */
    function goToStep(step, mode) {
      steps.forEach(function (stepEl) {
        stepEl.hidden = stepEl.getAttribute('data-uxkid-reco-step') !== step;
      });
      panel.setAttribute('data-uxkid-reco-current-step', step);
      if (mode === 'dialog') {
        if (dialogLabelMain) dialogLabelMain.textContent = DIALOG_LABELS[step] || '';
        if (dialogLabelExtra) dialogLabelExtra.textContent = step === 'result' ? ' · Tu plan recomendado' : '';
      } else {
        if (dialogLabelMain) dialogLabelMain.textContent = '';
        if (dialogLabelExtra) dialogLabelExtra.textContent = '';
      }
      if (content) content.scrollTop = 0;
    }

    /* Busca y muestra el pack (goal+versión resuelta) del resultado;
       si falta esa versión exacta, usa la otra versión del mismo goal
       ("si falta la versión pedida usa la otra versión del mismo
       objetivo"). Además prende solo los moduladores de P1/P2 que
       corresponden a las respuestas actuales (freq solo se respondió de
       verdad en mobile; en diálogo llega `null` y esos moduladores quedan
       ocultos). Devuelve true si encontró algún pack para mostrar. */
    function showResult(state) {
      var version = UxkidReco.resolveVersion(state.start, state.freq);
      var matched = null;
      var fallback = null;
      packs.forEach(function (el) {
        el.hidden = true;
        if (el.getAttribute('data-goal') === state.goal) {
          if (el.getAttribute('data-version') === version) matched = el;
          else fallback = el;
        }
      });
      var toShow = matched || fallback;
      if (!toShow) return false;
      toShow.hidden = false;
      toShow.querySelectorAll('[data-mod-freq]').forEach(function (el) {
        el.hidden = el.getAttribute('data-mod-freq') !== state.freq;
      });
      toShow.querySelectorAll('[data-mod-start]').forEach(function (el) {
        el.hidden = el.getAttribute('data-mod-start') !== state.start;
      });
      return true;
    }

    /* Renderiza el sheet/diálogo completo según el estado: resultado (si
       corresponde), barra de progreso del paso activo (según su modo) y,
       en "freq"/"start", opciones premarcadas. El paso "email" no
       necesita premarcado (es un form); "result" tampoco (no tiene
       opciones de respuesta propias, solo el CTA que en diálogo avanza a
       "email" — ver el handler de opciones más abajo). */
    function render(state) {
      if (state.step === 'result') showResult(state);
      var stepEl = sheet.querySelector('[data-uxkid-reco-step="' + state.step + '"]');
      if (stepEl) {
        updateProgress(stepEl, state.step, state.mode);
        if (state.step === 'freq') premarkOptions(stepEl, 'freq', state.freq);
        if (state.step === 'start') premarkOptions(stepEl, 'start', state.start);
      }
      syncSegVisibility(state.mode);
      goToStep(state.step, state.mode);
    }

    /* getInitialFocusTarget — a qué elemento llevar el foco al abrir, según
       el paso que quedó visible. Pasos "freq"/"start": la primera opción.
       Paso "email": el campo de email. Paso "result": el X de la
       cabecera, único y fijo (antes vivía un X por pack, y antes de eso
       ni siquiera era alcanzable desde los pasos previos) — se fuerza así
       aunque el CTA de "result" también lleve data-uxkid-reco-option (ver
       el handler más abajo): no autofocusear un botón de avance/compra al
       abrir el diálogo/sheet, decisión deliberada de accesibilidad. Si no
       hay nada enfocable, cae al panel (tabindex="-1"). */
    function getInitialFocusTarget(step) {
      if (step === 'result') return headerCloseBtn || panel;
      var stepEl = sheet.querySelector('[data-uxkid-reco-step="' + step + '"]');
      var target = stepEl && (stepEl.querySelector('[data-uxkid-reco-option]') || stepEl.querySelector('input'));
      if (!target) target = headerCloseBtn;
      return target || panel;
    }

    /* ---------- Abrir / cerrar (mismo patrón que balance-pdp.js initSheet) ---------- */
    function open(state) {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      render(state);
      lastFocus = document.activeElement;
      sheet.hidden = false;
      void sheet.offsetHeight; // fuerza reflow para que la transición de entrada corra
      sheet.classList.add('is-open');
      document.body.classList.add('uxkid-reco-sheet-open');
      var focusTarget = getInitialFocusTarget(state.step);
      if (focusTarget && focusTarget.focus) focusTarget.focus();
    }

    function close() {
      sheet.classList.remove('is-open');
      document.body.classList.remove('uxkid-reco-sheet-open');
      var done = function () {
        closeTimer = null;
        if (sheet.classList.contains('is-open')) return; // se reabrió durante el cierre
        sheet.hidden = true;
      };
      if (global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        done();
      } else {
        closeTimer = setTimeout(done, 350);
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /* ---------- Chips: abren el sheet en el paso que corresponda, según
       el modo actual (mobile "freq" o diálogo "start"). ---------- */
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var goal = chip.getAttribute('data-goal');
        UxkidReco.saveGoal(goal);
        markSelectedChip(goal);
        var mode = isDialogMode() ? 'dialog' : 'sheet';
        var saved = UxkidReco.loadState();
        var state = UxkidReco.resumeState(goal, saved, mode);
        UxkidReco.saveState(state);
        open(state);
      });
    });

    /* Premarca (sin abrir nada) el chip del wb_goal guardado, al cargar. */
    var storedGoal = UxkidReco.loadGoal();
    if (storedGoal) markSelectedChip(storedGoal);

    /* ---------- Opciones de "freq"/"start", y CTA de "result": tap =
       responde y avanza. El submit real del form de email (botón
       type="submit") NO pasa por acá: navega de verdad (ver comentario en
       el snippet y el chequeo de reanudación más abajo).
       El CTA de "result" (data-question="result" data-value="cta", ver
       snippet) es un caso especial: SOLO intercepta el click para avanzar
       a "email" en modo diálogo, cuando ese paso está activo y el usuario
       todavía no se suscribió; en mobile, o sin esas condiciones, se deja
       el comportamiento nativo del elemento (es un <a href> real al pack,
       o un <button> inerte sin acción) y esta función no llega a llamar
       advanceState.
       isDialogMode() se vuelve a evaluar en cada click (no solo al abrir):
       si el modo actual difiere del que quedó grabado en el estado (el
       usuario resizeó la ventana con el diálogo/sheet todavía abierto,
       caso raro pero posible), el click de un paso que ya no existe en el
       modo nuevo NO se aplica: se reinicia el flujo en el primer paso de
       ESE modo, igual que haría resumeState al reabrir. ---------- */
    sheet.querySelectorAll('[data-uxkid-reco-option]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var question = btn.getAttribute('data-question');
        var value = btn.getAttribute('data-value');
        var current = UxkidReco.loadState();
        if (!current) return; // guard: no debería poder responder sin haber tocado un chip antes
        var mode = isDialogMode() ? 'dialog' : 'sheet';
        if (mode !== current.mode) {
          var reset = UxkidReco.resumeState(current.goal, current, mode);
          UxkidReco.saveState(reset);
          render(reset);
          return;
        }
        if (question === 'result') {
          if (mode !== 'dialog' || !emailEnabled || current.completed) return; // deja el link real / botón inerte
          e.preventDefault();
        }
        var next = UxkidReco.advanceState(current, question, value, emailEnabled);
        UxkidReco.saveState(next);
        render(next);
      });
    });

    /* ---------- Cierre: backdrop, X, Escape (sin perder progreso: el
       estado ya quedó guardado en sessionStorage en cada paso) ---------- */
    sheet.querySelectorAll('[data-uxkid-reco-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !sheet.hidden) close();
    });

    /* ---------- Foco: trampa simple dentro del panel mientras está abierto ---------- */
    sheet.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || sheet.hidden) return;
      // El backdrop es <button> (ver snippet) pero vive fuera de `panel`, así
      // que este selector no lo alcanzaría igual; se excluye por clase también
      // para que el trap nunca lo tome si el markup cambiara.
      var focusable = panel.querySelectorAll('a[href], button:not([disabled]):not(.uxkid-reco-sheet__backdrop), input, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var list = Array.prototype.filter.call(focusable, function (el) { return el.offsetParent !== null; });
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    /* ---------- Swipe down para descartar (handle/cabecera siempre;
       contenido solo si scrollTop = 0, ahora que el scroller real es
       [data-uxkid-reco-content] y no el panel). Umbral de descarte ~80px.
       Pointer Events cubre touch + mouse.

       Bug corregido (Manuel, "hay que tocar la X dos veces"): en touch, un
       toque real mueve el dedo 1-3px aunque la intención sea solo tocar un
       botón. Si ya en el primer pixel de movimiento tratábamos eso como
       drag y llamábamos preventDefault() en el touchmove, el navegador
       suprime los eventos de mouse sintetizados de ESE toque —click
       incluido—, así que la X (o cualquier opción/CTA) necesitaba un
       segundo toque para disparar su propio click. Dos frenos para esto:
       1) canStartDrag descarta cualquier target dentro de un control
          interactivo (botón, link, opción, X): ahí NUNCA arranca el drag.
       2) SLOP: el gesto queda "pending" (sin mover el panel ni bloquear
          nada) hasta pasar 8px de desplazamiento; recién ahí es "active"
          (resolveDragPhase) y touchmove empieza a poder preventDefault
          (shouldPreventTouchScroll). Un tap real casi nunca supera 8px. ---------- */
    (function initDrag() {
      var THRESHOLD = 80; // px para que soltar cierre el sheet
      var SLOP = 8;        // px para que un toque pase de "tap" a "drag real"
      var pointerActive = false; // entre pointerdown y pointerup/cancel
      var dragging = false;      // true solo cuando ya superó el SLOP
      var startY = 0;
      var deltaY = 0;
      var lastTouchY = null;

      // En modo diálogo (≥768px, ver CSS) el panel está centrado en la
      // pantalla, no pegado al borde inferior: el gesto de arrastre no
      // aplica ahí (pedido de Manuel, 2026-09-21) — ni para cerrar ni para
      // mover el panel. Reutiliza el isDialogMode() de initRecommender
      // (mismo criterio que usa el estado para elegir modo/flujo, antes
      // duplicado acá): se re-evalúa en cada intento de arranque, no una
      // sola vez al cargar, por si el viewport cambia de tamaño.
      function canStartDrag(target) {
        if (isDialogMode()) return false;
        // Nunca arrancar el drag desde un control interactivo: ahí el tap
        // tiene que llegar limpio (X, opciones de paso 1/2, CTA del resultado).
        if (target.closest('button, a, [data-uxkid-reco-close], [data-uxkid-reco-option]')) return false;
        if (target.closest('[data-uxkid-reco-drag]')) return true;
        if (content && content.contains(target) && content.scrollTop <= 0) return true;
        return false;
      }

      function releaseDrag() {
        pointerActive = false;
        dragging = false;
        panel.classList.remove('is-dragging');
        panel.style.removeProperty('--uxkid-reco-drag-y');
        deltaY = 0;
        lastTouchY = null;
      }

      function onPointerDown(e) {
        if (sheet.hidden || !canStartDrag(e.target)) return;
        // "Pending": todavía no es un drag visual. No tocar el panel ni
        // sumar la clase is-dragging hasta pasar el slop (ver onPointerMove).
        pointerActive = true;
        dragging = false;
        startY = e.clientY;
        deltaY = 0;
        lastTouchY = null;
      }

      function onPointerMove(e) {
        if (!pointerActive) return;
        deltaY = Math.max(0, e.clientY - startY);
        var phase = UxkidReco.resolveDragPhase(deltaY, SLOP);
        if (phase === 'active' && !dragging) {
          dragging = true;
          panel.classList.add('is-dragging');
        }
        if (dragging) {
          panel.style.setProperty('--uxkid-reco-drag-y', deltaY + 'px');
        }
      }

      function onPointerUp() {
        if (!pointerActive) return;
        var shouldClose = dragging && deltaY > THRESHOLD;
        releaseDrag();
        if (shouldClose) close();
      }

      /* En touch, el navegador puede llevarse el gesto (scroll/back-swipe,
         pointercancel) antes de llegar al umbral de 80px. Este listener NO
         pasivo frena ese gesto nativo SOLO cuando el drag ya es "active"
         (shouldPreventTouchScroll) y va hacia abajo desde arriba del todo
         del contenido; nunca durante la fase "pending" (ahí sería lo que
         rompe el click). Si el dedo invierte y va hacia arriba, soltamos
         el drag para no pelear con el scroll nativo del contenido. */
      function onTouchMoveNative(e) {
        if (!pointerActive) return;
        var touch = e.touches && e.touches[0];
        if (!touch) return;
        var movingUp = lastTouchY !== null && touch.clientY < lastTouchY;
        lastTouchY = touch.clientY;
        if (movingUp) {
          releaseDrag();
          return;
        }
        var phase = UxkidReco.resolveDragPhase(deltaY, SLOP);
        var scrollTop = content ? content.scrollTop : 0;
        if (UxkidReco.shouldPreventTouchScroll(phase, deltaY, scrollTop)) {
          e.preventDefault();
        }
      }

      panel.addEventListener('pointerdown', onPointerDown);
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerUp);
      panel.addEventListener('touchmove', onTouchMoveNative, { passive: false });
    }());

    /* ---------- Reanudar tras el submit real del form de email ----------
       El form 'customer' (paso "email", SOLO existe en modo diálogo) hace
       un POST nativo: la página recarga. `sessionStorage` sobrevive a ese
       reload en la misma pestaña, así que si al iniciar encontramos el
       estado guardado en "email" (o incluso ya en "result", por un doble
       submit) Y el snippet renderizó el marcador [data-uxkid-reco-email-ok]
       (Shopify confirmó `form.posted_successfully?` para este contexto),
       avanzamos el estado a "result" y reabrimos solo, sin que el usuario
       pierda su objetivo ni tenga que volver a tocar el chip. `savedAfterEmail.mode`
       ya viene grabado en 'dialog' (el único modo donde se llega a
       "email"), así que advanceState() toma la rama correcta. Try/catch:
       si algo del estado guardado es inconsistente, no rompe la carga de
       la página. */
    try {
      var emailOkMarker = sheet.querySelector('[data-uxkid-reco-email-ok]');
      if (emailOkMarker) {
        var savedAfterEmail = UxkidReco.loadState();
        if (savedAfterEmail && (savedAfterEmail.step === 'email' || savedAfterEmail.step === 'result')) {
          var resumedAfterEmail = UxkidReco.advanceState(savedAfterEmail, 'email', 'subscribed', emailEnabled);
          UxkidReco.saveState(resumedAfterEmail);
          markSelectedChip(resumedAfterEmail.goal);
          open(resumedAfterEmail);
        }
      }
    } catch (err) {
      // No hay nada crítico que hacer acá: en el peor caso el usuario ve la
      // home normal y puede volver a tocar el chip.
    }
  }

  function init() {
    document.querySelectorAll('[data-uxkid-reco-root]').forEach(initRecommender);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}(typeof self !== 'undefined' ? self : this));
